import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { FileText, CheckCircle2, BookOpen, Music, Palette, Code, Camera, Film, ArrowRight, MessageCircle } from 'lucide-react';
import ChatPopup from '../components/ChatPopup';
import { copyrightProcessSteps } from '../mockData';

const CopyrightRegistration = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  const benefits = [
    'Public record of your ownership',
    'Ability to sue in federal court',
    'Statutory damages and attorney fees',
    'Protection against infringement',
    'Increased licensing opportunities',
    'Enhanced credibility',
    'Global protection through treaties',
    'Valuable asset for your business'
  ];

  const copyrightableWorks = [
    { icon: BookOpen, title: 'Literary Works', desc: 'Books, articles, manuscripts, and written content' },
    { icon: Music, title: 'Musical Works', desc: 'Songs, compositions, and musical scores' },
    { icon: Palette, title: 'Artistic Works', desc: 'Paintings, drawings, sculptures, and graphics' },
    { icon: Code, title: 'Software', desc: 'Computer programs and source code' },
    { icon: Camera, title: 'Photography', desc: 'Photographs and visual images' },
    { icon: Film, title: 'Audiovisual', desc: 'Films, videos, and multimedia content' }
  ];

  // Using processSteps from mockData

  const faqs = [
    {
      question: 'What can be copyrighted?',
      answer: 'Literary works, music, art, photographs, software, and other original creative works can be copyrighted.'
    },
    {
      question: 'How long does copyright registration take?',
      answer: 'Copyright registration typically takes 3-6 months, though expedited processing may be available for an additional fee.'
    },
    {
      question: 'Do I automatically have copyright?',
      answer: 'Yes, copyright exists from the moment of creation. However, registration is required to sue for infringement and claim statutory damages.'
    },
    {
      question: 'How long does copyright protection last?',
      answer: 'For works created after 1978, copyright lasts for the life of the author plus 70 years.'
    }
  ];

  return (
    <div className="min-h-screen">
      <ChatPopup isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-amber-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block mb-4">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto">
                <FileText className="h-10 w-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Copyright Registration Services
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Protect your creative works with official copyright registration. Secure your intellectual property rights for books, music, art, software, and more.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => setIsChatOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
                Register My Copyright <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" onClick={() => setIsChatOpen(true)} variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
                <MessageCircle className="mr-2 h-4 w-4" /> Talk to an Expert Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">What is Copyright Registration?</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-base">
                <p>
                  Copyright is a form of intellectual property protection that grants creators exclusive rights to their original works of authorship. This includes literary, musical, dramatic, artistic, and certain other intellectual works.
                </p>
                <p>
                  While copyright protection exists from the moment of creation, registering your work with the U.S. Copyright Office provides important legal advantages, including the ability to sue for infringement and claim statutory damages.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1544717297-fa95b6ee9643" 
                alt="Professional office environment for copyright registration services"
                className="rounded-2xl shadow-2xl w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Types of Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Can Be Copyrighted?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We help protect a wide range of creative works
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {copyrightableWorks.map((work, index) => {
              const Icon = work.icon;
              return (
                <Card key={index} className="border-2 border-gray-200 hover:border-orange-600 transition-colors">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{work.title}</h3>
                    <p className="text-gray-600">{work.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Key Benefits</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Protect your creative works and gain valuable legal rights
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg">
                <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Our streamlined process makes registration simple and efficient
            </p>
          </div>
          <div className="max-w-4xl mx-auto relative">
            {/* Connecting Line */}
            <div className="absolute left-[60px] top-[80px] bottom-[80px] w-1 bg-gradient-to-b from-orange-600 via-orange-500 to-amber-600"></div>
            
            <div className="space-y-12">
              {copyrightProcessSteps.map((step) => (
                <div key={step.step} className="relative">
                  {/* Step Number Badge */}
                  <div className="absolute left-0 w-[120px] h-[120px]">
                    <div className="relative w-full h-full">
                      <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl rotate-6 opacity-20"></div>
                      <div className="relative bg-gradient-to-br from-orange-600 to-amber-600 rounded-2xl w-full h-full flex items-center justify-center shadow-xl">
                        <span className="text-4xl font-bold text-white">{step.step}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content Card */}
                  <div className="ml-[160px] bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-orange-100 hover:border-orange-300">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                        <p className="text-gray-600 text-base leading-relaxed">{step.description}</p>
                      </div>
                      <div className="ml-6 flex-shrink-0">
                        <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center">
                          <CheckCircle2 className="h-8 w-8 text-orange-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-gray-50 border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FileText className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Protect Your Creative Work?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Get started today with our professional copyright registration services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => setIsChatOpen(true)} className="bg-white text-orange-600 hover:bg-gray-100">
              Register My Copyright <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" onClick={() => setIsChatOpen(true)} variant="outline" className="border-white text-white hover:bg-white/10">
              <MessageCircle className="mr-2 h-4 w-4" /> Talk to an Expert Now
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CopyrightRegistration;
